import { Pipe, PipeTransform, SecurityContext, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Marked } from 'marked';

// Renders Markdown with `marked` (GFM). breaks:true keeps single line breaks so a name+description
// pair renders as one item, not a restarted list. Safety: marked doesn't sanitize, so output goes
// through DomSanitizer and we return a plain string (never bypassed SafeHtml) so the [innerHTML]
// binding sanitizes again, which a discussion body of 20 000 customer-typed characters relies on.
const marked = new Marked({ gfm: true, breaks: true, async: false });

@Pipe({
  name: 'markdown',
  standalone: false,
  pure: true,
})
export class MarkdownPipe implements PipeTransform {
  private readonly sanitizer = inject(DomSanitizer);

  // Use 'link' for text a model or visitor can steer: an image is fetched unclicked and its address can carry data out.
  transform(value: string | null | undefined, images: 'show' | 'link' = 'show'): string {
    if (!value) {
      return '';
    }
    // Add the missing space after an ordered marker ("1.Name" -> "1. Name"); the lookahead spares
    // decimals like "10.5%".
    const normalized = value.replace(/^(\s*\d+\.)(?=[^\s\d])/gm, '$1 ');
    const rendered = marked.parse(normalized) as string;
    const sanitized = this.sanitizer.sanitize(SecurityContext.HTML, rendered) ?? '';
    const safe = images === 'link' ? imagesAsLinks(sanitized) : sanitized;
    // Only external links get target=_blank; same-origin links are left for the SPA click handler.
    return safe.replace(/<a\s+href="([^"]*)"/g, (match, href: string) =>
      isExternalLink(href) ? `<a href="${href}" target="_blank" rel="noopener noreferrer"` : match,
    );
  }
}

// Unsanitized is safe here: an inert <template> loads and runs nothing, and only its text is read.
export function markdownToText(value: string): string {
  const template = document.createElement('template');
  template.innerHTML = marked.parse(value) as string;
  return (template.content.textContent ?? '').replace(/\s+/g, ' ').trim();
}

// A <template>'s content is inert, so parsing the images here doesn't fetch them.
function imagesAsLinks(html: string): string {
  if (!html.includes('<img')) {
    return html;
  }
  const template = document.createElement('template');
  template.innerHTML = html;
  for (const image of Array.from(template.content.querySelectorAll('img'))) {
    const source = image.getAttribute('src');
    const label = image.getAttribute('alt') || source || '';
    if (!source || image.closest('a')) {
      image.replaceWith(label);
      continue;
    }
    const link = document.createElement('a');
    link.setAttribute('href', source);
    link.textContent = label;
    image.replaceWith(link);
  }
  return template.innerHTML;
}

// External = resolves to a different origin; unparseable is treated as internal.
function isExternalLink(href: string): boolean {
  try {
    return new URL(href, window.location.origin).origin !== window.location.origin;
  } catch {
    return false;
  }
}
